/**
 * Form helpers for create/edit level views.
 */
import type { CreateLevelDto, EducationCycle, Level, UpdateLevelDto } from '@/types/level.types';
import { getErrorMessage } from '@/utils/errors-utils';

/** Drag-list row; placeholder is the unsaved create item. */
export interface LevelSortableItem {
  id: string;
  code: string;
  label: string;
  isPlaceholder?: boolean;
}

/** Empty create form; cycle and ages stay unset until the user fills them. */
export function emptyCreateLevelForm(): CreateLevelDto {
  return {
    code: '',
    label: '',
    educationCycle: undefined,
    minAge: undefined,
    maxAge: undefined,
    status: 'active',
  };
}

/** Patch one field. */
export function applyLevelFormField<T extends CreateLevelDto | UpdateLevelDto>(
  form: T,
  field: keyof T,
  value: string | number | undefined,
): T {
  return { ...form, [field]: value };
}

/** Empty string → `undefined` (omit from the DTO). */
export function parseOptionalInt(value: string): number | undefined {
  return value ? Number.parseInt(value, 10) : undefined;
}

/** Empty {@link Select} value → `undefined` (omit from the DTO). */
export function resolveEducationCycle(value: string): EducationCycle | undefined {
  return value ? (value as EducationCycle) : undefined;
}

/** Hydrate the edit form from GET level. */
export function levelToFormData(level: Level): UpdateLevelDto {
  return {
    code: level.code,
    label: level.label,
    educationCycle: level.educationCycle || undefined,
    minAge: level.minAge || undefined,
    maxAge: level.maxAge || undefined,
    status: level.status,
  };
}

/** Swap the create placeholder id for the real id after POST (keeps drag order). */
export function resolveCreatedLevelOrder(
  orderedLevelIds: string[] | null,
  placeholderId: string,
  createdId: string,
): string[] | null {
  if (!orderedLevelIds) return null;
  return orderedLevelIds.map((id) => (id === placeholderId ? createdId : id));
}

/** Existing levels plus a create placeholder when `isCreate`. */
export function buildLevelSortableItems(
  levels: Array<Pick<Level, 'id' | 'code' | 'label'>>,
  isCreate: boolean,
  newLevelLabel?: string,
  placeholderId = '__new__',
): LevelSortableItem[] {
  const existing: LevelSortableItem[] = levels.map((l) => ({
    id: l.id,
    code: l.code,
    label: l.label,
  }));
  if (isCreate) {
    return [
      ...existing,
      {
        id: placeholderId,
        code: '',
        label: newLevelLabel?.trim() || 'Nouveau niveau',
        isPlaceholder: true,
      },
    ];
  }
  return existing;
}

/** Re-validate `maxAge` after `minAge` changes. */
export function getLevelFieldsToRevalidateAfterChange<T extends { maxAge?: number }>(
  field: PropertyKey,
  form: T,
): Array<keyof T & string> {
  if (field === 'minAge' && form.maxAge !== undefined) return ['maxAge'];
  return [];
}

/** Central error copy from {@link getErrorMessage}. */
export function getLevelApiError(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}
